import React, { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { Modal, notify } from 'app/components';
import { QUESTIONS_TEMPLATE } from '../../mockData';
import SearchForm from './searchForm';
import Form from './form';
import './style';
import { EMAIL_TYPE, MULTIPLE_CHOICE_TYPE, NON_OPTION_CONFIGS, SUB_QUESTION_ALLOWED_TYPES } from '../../constants';
import { filterValidOptions, getSanitizedConfig } from '../../utils';

const QuestionBuilder = (props) => {
  const {
    onCloseModal,
    isOpen,
    ticketsConfig,
    onAddQuestion,
    selectedQuestion,
    list,
  } = props;

  const [questions, setQuestions] = useState(QUESTIONS_TEMPLATE);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [config, setConfig] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const savedQuestionConfig = useRef(null);

  useEffect(() => {
    if (selectedQuestion) {
      setConfig(selectedQuestion);
      savedQuestionConfig.current = selectedQuestion;
      setShowQuestionForm(true);
    }
  }, [selectedQuestion]);

  const onAddCustomQuestion = (text) => {
    setConfig({ label: text, is_question_per_transaction: true });
    savedQuestionConfig.current = { label: text, is_question_per_transaction: true };
    setShowQuestionForm(true);
  };

  const onClose = () => {
    if (showDiscardModal) setShowDiscardModal(false);
    if (showQuestionForm) setShowQuestionForm(false);
    setConfig(null);
    savedQuestionConfig.current = null;
    setErrors(null);
    onCloseModal();
  };

  const onFormChange = (key, value, type = null) => {
    setConfig((prev) => {
      let rest = {};
      if (key === 'type') {
        if (([EMAIL_TYPE, ...NON_OPTION_CONFIGS].includes(value) || prev?.type === EMAIL_TYPE)) {
          rest = {
            conditions: [],
            options: [],
            showSpecific: false,
          };
        } else if (prev?.type === MULTIPLE_CHOICE_TYPE && value !== MULTIPLE_CHOICE_TYPE) {
          rest = {
            options: prev?.options?.filter((opt) => !opt?.others) || [],
          };
        }
      } else if (key === 'options' && type === 're-arrange') {
        const otherOption = prev?.options?.filter((opt) => opt?.others);
        const updatedOptions = otherOption?.length ? [...value, ...otherOption] : value;
        rest = {
          options: updatedOptions,
        };
      }
      return {
        ...prev,
        [key]: value,
        ...rest,
      };
    });
  };

  const onClickAddQuestion = (questionConfig) => {
    const { label, type } = questionConfig;
    const addedConfig = { label, type, is_question_per_transaction: true };
    setConfig(addedConfig);
    savedQuestionConfig.current = addedConfig;
    setShowQuestionForm(true);
  };

  const onSave = () => {
    let errors = {};
    if (!config?.label?.trim()) {
      errors = { label: 'Enter question to proceed' };
    } else {
      const otherQuestions = list?.filter((opt) => opt?.id !== config?.id);
      const existingLabels = otherQuestions?.map((opt) => opt?.label);

      if (existingLabels?.includes(config?.label)) errors = { label: 'A question with this label already exists!' };
    }

    if (!config?.type) errors = { ...errors, type: 'Required field' };

    if (SUB_QUESTION_ALLOWED_TYPES.includes(config?.type)) {
      if (filterValidOptions(config?.options).length < 2) {
        notify('error', 'Please add at least two options to proceed!', {
          position: 'bottom-center',
          className: 'question-modal-error',
          duration: 5000,
          style: { maxWidth: 'fit-content' },
        });
        errors = { ...errors, options: 'Please add at least two options to proceed!' };
      } else {
        const filteredOptions = filterValidOptions(config?.options);
        const options = new Set(filteredOptions?.map((opt) => opt?.value));
        if (options.size !== filteredOptions?.length) {
          notify('error', 'Remove Duplicate options', {
            position: 'bottom-center',
            className: 'question-modal-error',
            duration: 5000,
            style: { maxWidth: 'fit-content' },
          });
          errors = { ...errors, options: 'Remove Duplicate options' };
        }
      }
    }

    if (config?.type === EMAIL_TYPE && config?.showSpecific && !filterValidOptions(config?.options).length) {
      notify('error', 'Please add at least one mail ID to proceed!', {
        position: 'bottom-center',
        className: 'question-modal-error',
        duration: 5000,
        style: { maxWidth: 'fit-content' },
      });
      errors = { ...errors, options: 'Please add at least one mail ID to proceed!' };
    }

    if (Object.keys(errors).length) {
      setErrors(errors);
      return;
    }
    onAddQuestion(getSanitizedConfig(config));
    onClose();
  };

  const onCancel = () => {
    if (!isEqual(savedQuestionConfig.current, config)) setShowDiscardModal(true);
    else onClose();
  };

  return (
    <div className="question-builder-wrapper">
      <Modal
        className="question-modal"
        size="large"
        title="Ask a question"
        hasClose={true}
        isOpen={isOpen}
        onClose={onCancel}
        {...showQuestionForm && ({
          onClickPrimaryCta: onSave,
          onClickSecondaryCta: onCancel,
        })}
      >
        {showQuestionForm ? (
          <Form
            formConfig={config}
            ticketsConfig={ticketsConfig}
            onChange={onFormChange}
            errors={errors}
            setErrors={setErrors}
          />
        ) : (
          <SearchForm
            questions={questions}
            onAddQuestion={onClickAddQuestion}
            onAddCustomQuestion={onAddCustomQuestion}
          />)}
      </Modal>
      <Modal
        className="delete-confirmation-modal"
        primaryText="Discard"
        secondaryText="Continue editing"
        size="regular"
        isOpen={showDiscardModal}
        onClickPrimaryCta={onClose}
        onClickSecondaryCta={() => setShowDiscardModal(false)}
        title="Are you sure you want to discard the changes?"
        headerClassName='flex-1'
        footerClassName='flex-1'
      />
    </div>
  );
};

export default QuestionBuilder;
